import React, { useEffect, useRef, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import type { SelectProps } from 'antd';
import { useIntl } from 'react-intl';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { Country } from 'entities/countries/model/types';
import { general, generalFields } from 'shared/model/schemas/rules';

export type PhoneInputState = {
  countryCode: number | undefined;
  phoneNumber: string;
  haveErrors: boolean;
};

type PhoneInputProps = {
  countries: Country[];
  onChange: (state: PhoneInputState) => unknown;
  forceShowErrors?: boolean;
  value?: PhoneInputState;
};

const PhoneInput: React.FC<PhoneInputProps> = ({ countries, onChange, forceShowErrors = false, value }) => {
  const [state, setState] = useState<PhoneInputState>(() => {
    if (value) {
      return {
        countryCode: value.countryCode,
        phoneNumber: value.phoneNumber,
        haveErrors: value.haveErrors ?? true,
      };
    }
    return {
      countryCode: undefined,
      phoneNumber: '',
      haveErrors: true,
    };
  });
  const { formatMessage: t } = useIntl();
  const [form] = Form.useForm();
  const onChangeRef = useRef(onChange);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string[]}>({
    phoneCountry: [],
    phoneNumber: []
  });
  const [touched, setTouched] = useState({
    country: false,
    phone: false,
  });

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const onFieldsChange = () => {
    setValidationErrors(prevState => ({
      ...prevState,
      phoneCountry: form.getFieldError('countryCode'),
      phoneNumber: form.getFieldError('phoneNumber'),
    }));
  };

  const phoneNumberValidator = async (_: any, phoneValue: string) => {
    const countryId = form.getFieldValue('countryCode');
    const country = countries.find(c => c.id === countryId);
    if (!country) {
      return Promise.reject(new Error(t({id: "schemas.validation.phone.country.required"})));
    }
    const phoneNumber = parsePhoneNumberFromString(`+${country.phoneCode}${phoneValue}`);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return Promise.reject(new Error(t({id: "schemas.validation.phone.invalid"})));
    }
    return Promise.resolve();
  };

  const onChangeCountryCode = (countryValue: number) => {
    setState((prev) => ({
      ...prev,
      countryCode: countryValue,
    }));
    setTouched((prev) => ({ ...prev, country: true }));
  };

  const onChangePhoneNumber = (number: string) => {
    setState((prev) => ({
      ...prev,
      phoneNumber: number,
    }));
    setTouched((prev) => ({ ...prev, phone: true }));
  };

  const validationScheme = {
    countryCode: [general(t)['required-field']],
    phoneNumber: [
      general(t)['required-field'],
      ...generalFields(t).phoneNumber,
      { validator: phoneNumberValidator },
    ],
  };

  useEffect(() => {
    if (value && (value.countryCode !== state.countryCode || value.phoneNumber !== state.phoneNumber)) {
      setState({
        countryCode: value.countryCode,
        phoneNumber: value.phoneNumber,
        haveErrors: value.haveErrors ?? true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.countryCode, value?.phoneNumber]);

  useEffect(() => {
    form.setFieldsValue({
      countryCode: state.countryCode,
      phoneNumber: state.phoneNumber,
    });
  }, [state.countryCode, state.phoneNumber, form]);

  useEffect(() => {
    const shouldShowErrors = forceShowErrors || touched.country || touched.phone;
    if (shouldShowErrors) {
      form
        .validateFields()
        .then(() => {
          const newState = { ...state, haveErrors: false };
          onChangeRef.current(newState);
        })
        .catch(() => {
          const newState = { ...state, haveErrors: true };
          onChangeRef.current(newState);
        });
    } else {
      form
        .validateFields({ validateOnly: true })
        .then(() => {
          const newState = { ...state, haveErrors: false };
          onChangeRef.current(newState);
        })
        .catch(() => {
          const newState = { ...state, haveErrors: true };
          onChangeRef.current(newState);
        });
    }
  }, [state.countryCode, state.phoneNumber, form, forceShowErrors, touched, state]);

  const filterOption = (input: string, option?: { label: string; value: number; children?: React.ReactNode }) => {
    if (!option) return false;
    const searchText = input.toLowerCase();
    const country = countries.find(c => c.id === option.value);
    if (!country) return false;

    return (
      country.name.toLowerCase().includes(searchText) ||
      country.phoneCode.includes(searchText) ||
      `+${country.phoneCode}`.includes(searchText)
    );
  };

  const builtinPlacements: SelectProps['builtinPlacements'] = {
    bottomLeft: {
      points: ['tl', 'bl'],
      offset: [0, 4],
      overflow: {
        adjustX: true,
        adjustY: true,
      },
    },
  };

  return (
    <Form form={form} validateTrigger='onBlur' style={{ width: '100%' }} onFieldsChange={onFieldsChange}>
      <Row gutter={8}>
        <Col span={10}>
          <Form.Item
            help={
              <div>{validationErrors.phoneCountry[0]}</div>
            }
            rules={validationScheme.countryCode}
            name="countryCode"
          >
            <Select
              showSearch
              filterOption={filterOption}
              placement="bottomLeft"
              builtinPlacements={builtinPlacements}
              getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
              value={state.countryCode}
              popupMatchSelectWidth={false}
              optionLabelProp="label"
              onChange={onChangeCountryCode}
              placeholder="+000"
              style={{ height: 55, textAlign: 'left' }}
              dropdownStyle={{ textAlign: 'left', maxWidth: '80vw' }}
              listHeight={300}
              allowClear={false}
            >
              {countries.map((country) => (
                <Select.Option
                  label={`+${country.phoneCode}`}
                  value={country.id}
                  key={country.id}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <div
                      className={`fi fi-${country.iso2.toLowerCase()}`}
                      style={{ marginRight: 6, flex: 'none' }}
                    />
                    <div>
                      {`+${country.phoneCode} ${country.name}`}
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={14}>
          <Form.Item 
            help={
              <div style={{ paddingLeft: 50 }}>{validationErrors.phoneNumber[0]}</div>
            } 
            rules={validationScheme.phoneNumber} 
            name="phoneNumber"
          >
            <Input
              style={{ height: 55 }}
              placeholder="000-00-00"
              value={state.phoneNumber}
              onChange={(event) => {
                onChangePhoneNumber(event.target.value);
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default PhoneInput;
